/**
 * 星球圈模块：动态流、动态详情与评论线程（明确以宠物或主人身份）、宠物公开主页、关注、撤下、屏蔽、举报。
 * 动态只来自真实世界事件（到访）；fixture 模式的互动只在本页内存里演示。
 */
import type { BlockedTarget, BlockedTargetPage, Comment, CommentPage, FriendSummary, Post, PostPage } from "@/shared/contracts";
import type { SocialService, UnpublishPhotoResult } from "@/shared/services/types";
import { ApiError } from "@/shared/api/errors";
import { defineModule } from "@/shared/modules/types";
import { fixtureComments, fixtureFeed, fixturePetPosts, fixturePost, fixtureReact } from "@/fixtures/social";
import { delay } from "@/fixtures/world";
import { parseMyReports } from "@/features/me/myReports";
import { CirclePage, PetProfilePage, PostThreadPage } from "./pages";

/**
 * 演示里的「我屏蔽的」（WIRE-01 A6）：只记这一次演示里主人自己点过的屏蔽，不编以前的记录；换一次演示（重建服务）就清空。
 * 名字照后端的口径：动态的作者是那条动态上的名字，评论的作者用「一位你屏蔽过的用户」。
 */
function demoBlocks(): Pick<SocialService, "block" | "blockedTargets" | "unblock"> {
  const items: BlockedTarget[] = [];
  let seq = 0;
  return {
    block: async (target) => {
      let item: BlockedTarget = { target_kind: "account", target_ref: "", display_name: "一位你屏蔽过的用户", avatar_url: null, created_at: new Date().toISOString() };
      if (target.post_id) {
        try {
          item = { ...item, target_kind: "pet", display_name: fixturePost(target.post_id).author.display_name || "一只你屏蔽过的宠物" };
        } catch {
          item = { ...item, target_kind: "pet", display_name: "一只你屏蔽过的宠物" };
        }
      }
      seq += 1;
      items.unshift({ ...item, target_ref: `demo-block-${seq}` });
      return delay(undefined);
    },
    blockedTargets: async () => delay<BlockedTargetPage>({ items: [...items], next_cursor: null }),
    unblock: async (targetRef) => {
      const at = items.findIndex((item) => item.target_ref === targetRef);
      if (at >= 0) items.splice(at, 1);
      return delay(undefined);
    },
  };
}

export default defineModule({
  id: "social",
  routes: [
    { path: "circle", element: <CirclePage /> },
    { path: "posts/:postId", element: <PostThreadPage /> },
    { path: "pets/:petId", element: <PetProfilePage /> },
  ],
  services: {
    social: {
      fixture: () => ({
        feed: () => delay(fixtureFeed()),
        petPosts: (petId) => delay(fixturePetPosts(petId)),
        post: async (id) => delay(fixturePost(id)),
        comments: async (id) => delay({ items: fixtureComments(id), next_cursor: null }),
        react: async (id, _body, key) => delay(fixtureReact(id, key)),
        comment: async () => {
          throw ApiError.capability("social.reactions", "演示模式不发表评论。");
        },
        removePost: async () => {
          throw ApiError.capability("social.moderation", "演示模式不撤下动态。");
        },
        removeComment: async () => {
          throw ApiError.capability("social.moderation", "演示模式不删除留言。");
        },
        // 演示世界不分是哪只关注的：第三个参数 as_pet_id（FOLLOW-AS-PET-01）不看，只演示按钮。
        follow: async (_petId, _follow, _asPetId) => delay(undefined),
        // 屏蔽、我屏蔽的、解除（WIRE-01 A6）：同一份演示记录
        ...demoBlocks(),
        report: async () => delay(undefined),
        // 演示世界没有“真实遇见”的记录：不编朋友。
        friends: async () => {
          throw ApiError.capability("social.friends", "演示模式没有朋友记录。");
        },
        // 演示里的“举报”只是本页演示、不记下来，也没有运营后台：没有可查的举报记录，按能力未接入处理，不编记录；
        // 也不回空列表——空列表会被说成“你还没有举报过”，而演示里明明点得了“举报”。
        myReports: async () => {
          throw ApiError.capability("social.report_outcomes", "演示模式没有举报记录。");
        },
      }),
      live: ({ api }) => ({
        feed: (cursor) => api.request<PostPage>("/circle/feed", { query: { cursor } }),
        petPosts: (petId, cursor) => api.request<PostPage>(`/pets/${encodeURIComponent(petId)}/posts`, { query: { cursor } }),
        post: (id) => api.request<Post>(`/posts/${encodeURIComponent(id)}`),
        comments: (id, cursor) => api.request<CommentPage>(`/posts/${encodeURIComponent(id)}/comments`, { query: { cursor } }),
        react: (id, body, key) => api.request<Post>(`/posts/${encodeURIComponent(id)}/reactions`, { method: "POST", body, idempotencyKey: key }),
        comment: (id, body, key) => api.request<Comment>(`/posts/${encodeURIComponent(id)}/comments`, { method: "POST", body, idempotencyKey: key }),
        removePost: (id) => api.request<void>(`/posts/${encodeURIComponent(id)}`, { method: "DELETE" }),
        // 不再公开这一张（SOCIAL-PHOTO-REVOKE-UI-117）：只给发帖宠物所在家庭的管理员；同一张重试用同一个 operation_id
        unpublishPhoto: (postId, mediaId, operationId) => api.request<UnpublishPhotoResult>(
          `/social-media/posts/${encodeURIComponent(postId)}/media/${encodeURIComponent(mediaId)}/revocation`, { method: "POST", body: { operation_id: operationId } }),
        removeComment: (id) => api.request<void>(`/comments/${encodeURIComponent(id)}`, { method: "DELETE" }),
        // FOLLOW-AS-PET-01（I 10-09 定）：以自家哪只宠物的身份关注、取消，as_pet_id 是查询参数（后端 routers/web/social.py），body 照旧只有 follow。
        // 后端没收到这一项时，好几只的家回 403（提示还是错的“不能关注自己。”），所以缺了就在本地拒绝、不发请求（同 VISIT-LIVE-01 farmActorQuery）。
        follow: (petId, follow, asPetId) => {
          if (!asPetId) {
            return Promise.reject(new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "有了自己的宠物，就能关注 TA。", details: { reason: "follow_as_pet_required" } }));
          }
          return api.request<void>(`/pets/${encodeURIComponent(petId)}/follow`, { method: "POST", query: { as_pet_id: asPetId }, body: { follow } });
        },
        block: (target) => api.request<void>("/blocks", { method: "POST", body: { post_id: target.post_id ?? null, comment_id: target.comment_id ?? null } }),
        // 我屏蔽的（WIRE-01 A6）：游标作查询参数 cursor 原样带回，第一页不带；解除是 DELETE，引用原样转义进路径（后端找不到也 204）。
        blockedTargets: (cursor, signal) => api.request<BlockedTargetPage>("/blocks", { query: { cursor: cursor ?? undefined }, signal }),
        unblock: (targetRef) => api.request<void>(`/blocks/${encodeURIComponent(targetRef)}`, { method: "DELETE" }),
        report: (kind, id, reason) => api.request<void>("/reports", { method: "POST", body: { target_kind: kind, target_id: id, reason } }),
        friends: (petId, signal) => api.request<FriendSummary[]>("/friends", { query: { pet_id: petId }, signal }),
        // 契约有 MyReports，但路由还没关联 response_model：先按 unknown 取回，再逐条校验（坏条目丢掉，外层坏了抛可重试的错误）；没装运营后台时后端回能力未接入。
        myReports: async (signal) => parseMyReports(await api.request<unknown>("/reports/mine", { signal })),
      }),
    },
  },
});
